// File: src/services/omnicine/export/WavWriter.ts
//
// WavWriter — browser-safe WAV encoder (no deps)
// ---------------------------------------------
// Supports:
// - PCM 16-bit (most compatible)
// - IEEE Float 32-bit (optional; larger; not always accepted by players)
// - Mono/Stereo (or N channels)
// - Interleaved frames
//
// Primary entrypoints:
//   encodeWavFromFloat32Interleaved(...)
//   encodeWavFromAudioBuffer(...)
//
// Notes:
// - Input is assumed to be Float32 in [-1, +1] (typical WebAudio domain).
// - For PCM16 we hard-clip and scale to int16.
// - For Float32 WAV we can optionally clamp to [-1,+1] (default true for safety).
// - Optional deterministic TPDF dither for PCM16 (default off).
// - Optional PCM16 headroom scalar (default 0.9995) to reduce edge clipping.
//
// This module is intentionally "dumb I/O" (no resampling, no mixing, no normalization).

export type WavEncoding = "pcm16" | "f32";

export interface WavEncodeOptions {
  sampleRate: number;
  channels: number;
  encoding?: WavEncoding; // default: "pcm16"

  /**
   * PCM16 headroom scalar applied BEFORE quantization.
   * - Set < 1.0 to reduce edge clipping (recommended for podcast exports).
   * - Default: 0.9995
   */
  pcm16Peak?: number;

  /**
   * Deterministic TPDF dither for PCM16.
   * - Helps quiet fades/silence not sound "crunchy".
   * - Default: false
   */
  dither?: boolean;

  /**
   * Seed for deterministic dither RNG.
   * - Default: 0xC0FFEE
   */
  ditherSeed?: number;

  /**
   * For Float32 WAV, whether to clamp samples to [-1,+1].
   * - Default: true (keeps current safety behavior)
   */
  clampFloat?: boolean;
}

export interface WavWriteResult {
  mime: "audio/wav";
  encoding: WavEncoding;
  sampleRate: number;
  channels: number;
  frames: number;
  bytes: number;
  buffer: ArrayBuffer;
  blob: Blob;
}

/* =========================================================
 * Helpers
 * ======================================================= */

function clamp01Signed(x: number): number {
  // NaN/Infinity-safe clamp: poison samples become 0
  if (!Number.isFinite(x)) return 0;
  if (x > 1) return 1;
  if (x < -1) return -1;
  return x;
}

function assertInt(name: string, v: unknown) {
  if (!Number.isFinite(v as number) || Math.floor(v as number) !== (v as number)) {
    throw new Error(`WavWriter: ${name} must be an integer. Got: ${String(v)}`);
  }
}

function assertFinite(name: string, v: unknown) {
  if (!Number.isFinite(v as number)) {
    throw new Error(`WavWriter: ${name} must be finite. Got: ${String(v)}`);
  }
}

function writeAscii(dv: DataView, offset: number, s: string) {
  for (let i = 0; i < s.length; i++) dv.setUint8(offset + i, s.charCodeAt(i) & 0xff);
}

function framesFromInterleavedLength(len: number, channels: number): number {
  if (channels <= 0) throw new Error(`WavWriter: channels must be > 0. Got: ${channels}`);
  if (len < 0) throw new Error(`WavWriter: interleaved length must be >= 0. Got: ${len}`);
  if (len % channels !== 0) {
    throw new Error(`WavWriter: interleaved length (${len}) not divisible by channels (${channels}).`);
  }
  return len / channels;
}

function clampInt16(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n > 32767) return 32767;
  if (n < -32768) return -32768;
  return n;
}

/**
 * Simple deterministic xorshift32 RNG for optional dither.
 * Returns float in [0,1).
 */
function makeXorShift32(seed: number) {
  let x = (seed | 0) || 0xC0FFEE;
  return () => {
    // xorshift32
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    // Convert to [0,1)
    return ((x >>> 0) / 4294967296);
  };
}

/**
 * TPDF dither in LSB units for PCM16.
 * We add noise in roughly [-1, +1] LSB range (triangular PDF).
 */
function tpdfDitherLSB(rng: () => number): number {
  // two uniform(0,1) -> triangular(-1, +1)
  return (rng() - rng());
}

/**
 * PCM16 quantization:
 * - clamp to [-1,+1]
 * - apply headroom scalar (pcm16Peak <= 1)
 * - optional deterministic TPDF dither (in LSB units)
 * - map to int16 symmetric-ish
 */
function pcm16FromFloat(x: number, pcm16Peak: number, ditherLSB: number): number {
  const peak = Number.isFinite(pcm16Peak) ? pcm16Peak : 1;
  const p = peak > 0 ? Math.min(1, peak) : 1;

  // clamp & apply headroom
  const c = clamp01Signed(x) * p;

  // scale to int domain
  const scaled = c < 0 ? (c * 32768) : (c * 32767);

  // dither is in LSB units -> add directly before rounding
  const withDither = scaled + ditherLSB;

  return clampInt16(Math.round(withDither));
}

/* =========================================================
 * Core encoder (Float32 interleaved)
 * ======================================================= */

export function encodeWavFromFloat32Interleaved(
  interleaved: Float32Array,
  opts: WavEncodeOptions,
): WavWriteResult {
  const sampleRate = opts.sampleRate;
  const channels = opts.channels;
  const encoding: WavEncoding = opts.encoding ?? "pcm16";

  assertFinite("sampleRate", sampleRate);
  assertInt("channels", channels);

  if (sampleRate <= 0) throw new Error(`WavWriter: sampleRate must be > 0. Got: ${sampleRate}`);
  if (channels <= 0) throw new Error(`WavWriter: channels must be > 0. Got: ${channels}`);

  if (!(interleaved instanceof Float32Array)) {
    throw new Error("WavWriter: interleaved must be a Float32Array.");
  }

  const frames = framesFromInterleavedLength(interleaved.length, channels);

  const bytesPerSample = encoding === "pcm16" ? 2 : 4;
  const blockAlign = channels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataBytes = frames * blockAlign;

  // RIFF header is 44 bytes for PCM/F32 minimal format (no extra chunks).
  const headerBytes = 44;
  const totalBytes = headerBytes + dataBytes;

  const buffer = new ArrayBuffer(totalBytes);
  const dv = new DataView(buffer);

  // RIFF chunk
  writeAscii(dv, 0, "RIFF");
  dv.setUint32(4, 36 + dataBytes, true);
  writeAscii(dv, 8, "WAVE");

  // fmt subchunk
  writeAscii(dv, 12, "fmt ");
  dv.setUint32(16, 16, true); // PCM fmt chunk size
  const audioFormat = encoding === "pcm16" ? 1 : 3; // 1=PCM, 3=IEEE float
  dv.setUint16(20, audioFormat, true);
  dv.setUint16(22, channels, true);
  dv.setUint32(24, sampleRate, true);
  dv.setUint32(28, byteRate, true);
  dv.setUint16(32, blockAlign, true);
  dv.setUint16(34, bytesPerSample * 8, true);

  // data subchunk
  writeAscii(dv, 36, "data");
  dv.setUint32(40, dataBytes, true);

  // payload
  let o = headerBytes;

  const pcm16Peak = Number.isFinite(opts.pcm16Peak as number) ? Number(opts.pcm16Peak) : 0.9995;
  const wantDither = !!opts.dither;
  const clampFloat = opts.clampFloat !== false; // default true
  const rng = wantDither ? makeXorShift32(Number(opts.ditherSeed ?? 0xC0FFEE)) : null;

  if (encoding === "pcm16") {
    for (let i = 0; i < interleaved.length; i++) {
      const d = wantDither && rng ? tpdfDitherLSB(rng) : 0;
      dv.setInt16(o, pcm16FromFloat(interleaved[i] ?? 0, pcm16Peak, d), true);
      o += 2;
    }
  } else {
    for (let i = 0; i < interleaved.length; i++) {
      const v = interleaved[i] ?? 0;
      dv.setFloat32(o, clampFloat ? clamp01Signed(v) : (Number.isFinite(v) ? v : 0), true);
      o += 4;
    }
  }

  const blob = new Blob([buffer], { type: "audio/wav" });

  return {
    mime: "audio/wav",
    encoding,
    sampleRate,
    channels,
    frames,
    bytes: totalBytes,
    buffer,
    blob,
  };
}

/* =========================================================
 * Convenience: AudioBuffer → WAV
 * ======================================================= */

export interface AudioBufferLike {
  readonly sampleRate: number;
  readonly numberOfChannels: number;
  readonly length: number; // frames
  getChannelData(channel: number): Float32Array;
}

/**
 * Converts an AudioBuffer (or AudioBuffer-like) to interleaved Float32
 * and encodes a WAV.
 */
export function encodeWavFromAudioBuffer(
  audio: AudioBufferLike,
  opts?: {
    encoding?: WavEncoding;
    channels?: number;
    sampleRate?: number;
    pcm16Peak?: number;
    dither?: boolean;
    ditherSeed?: number;
    clampFloat?: boolean;
  },
): WavWriteResult {
  const sr = opts?.sampleRate ?? audio.sampleRate;
  const ch = opts?.channels ?? audio.numberOfChannels;
  const encoding = opts?.encoding ?? "pcm16";

  assertFinite("sampleRate", sr);
  assertInt("channels", ch);

  if (sr <= 0) throw new Error(`WavWriter: sampleRate must be > 0. Got: ${sr}`);
  if (ch <= 0) throw new Error(`WavWriter: channels must be > 0. Got: ${ch}`);

  const frames = audio.length;
  assertInt("frames", frames);
  if (frames < 0) throw new Error(`WavWriter: frames must be >= 0. Got: ${frames}`);

  const interleaved = new Float32Array(frames * ch);

  for (let c = 0; c < ch; c++) {
    const src = audio.getChannelData(c);
    if (!(src instanceof Float32Array)) {
      throw new Error(`WavWriter: getChannelData(${c}) must return Float32Array.`);
    }
    if (src.length !== frames) {
      throw new Error(`WavWriter: channel ${c} length mismatch. Expected ${frames}, got ${src.length}.`);
    }

    // write interleaved
    let w = c;
    for (let i = 0; i < frames; i++) {
      interleaved[w] = src[i] ?? 0;
      w += ch;
    }
  }

  return encodeWavFromFloat32Interleaved(interleaved, {
    sampleRate: sr,
    channels: ch,
    encoding,
    pcm16Peak: opts?.pcm16Peak,
    dither: opts?.dither,
    ditherSeed: opts?.ditherSeed,
    clampFloat: opts?.clampFloat,
  });
}

/* =========================================================
 * Utility: interleave planar channels
 * ======================================================= */

/**
 * Interleaves planar Float32 channel arrays into one interleaved buffer.
 * Useful if you already have per-channel Float32Arrays.
 */
export function interleaveFloat32(
  channels: Float32Array[],
  frames?: number,
): { interleaved: Float32Array; channels: number; frames: number } {
  const ch = channels.length;
  if (ch <= 0) throw new Error("WavWriter: interleaveFloat32 requires at least 1 channel.");

  const n = frames ?? channels[0]?.length ?? 0;
  assertInt("frames", n);
  if (n < 0) throw new Error(`WavWriter: frames must be >= 0. Got: ${n}`);

  for (let c = 0; c < ch; c++) {
    const len = channels[c]?.length ?? 0;
    if (len !== n) {
      throw new Error(`WavWriter: interleaveFloat32 channel length mismatch. ch0=${n}, ch${c}=${len}`);
    }
  }

  const out = new Float32Array(n * ch);
  for (let c = 0; c < ch; c++) {
    const src = channels[c];
    let w = c;
    for (let i = 0; i < n; i++) {
      out[w] = src[i] ?? 0;
      w += ch;
    }
  }

  return { interleaved: out, channels: ch, frames: n };
}
